import { useEffect, useState } from 'react';
import axiosInstance from 'shared/services/api/axiosInstance';

export function parseNumericTeamId(teamId) {
  if (typeof teamId === 'object' && teamId !== null) {
    return parseInt(teamId._id || teamId.id, 10);
  }
  if (typeof teamId === 'string') {
    try {
      const teamDataStr = localStorage.getItem('selectedTeamData');
      if (teamDataStr) {
        const teamData = JSON.parse(teamDataStr);
        return parseInt(teamData._id || teamData.id, 10);
      }
      return parseInt(teamId, 10);
    } catch {
      return parseInt(teamId, 10);
    }
  }
  return parseInt(teamId, 10);
}

export async function fetchUserPropertyOptions(teamId, property, { q, limit = 500, signal } = {}) {
  const numericTeamId = parseNumericTeamId(teamId);
  if (!numericTeamId || !property) return [];
  const prop = String(property).replace(/^user\./, '').replace(/^custom\./, '');
  const params = { team_id: numericTeamId, property: prop, limit };
  const trimmed = String(q || '').trim();
  if (trimmed) params.q = trimmed;
  const res = await axiosInstance.get('/events/user-property-options', { params, signal });
  if (res.data?.success && Array.isArray(res.data.options)) return res.data.options;
  return [];
}

export async function fetchEventPropertyOptions(teamId, property, { eventName, q, limit = 500, signal } = {}) {
  const numericTeamId = parseNumericTeamId(teamId);
  if (!numericTeamId || !property) return [];
  const params = { team_id: numericTeamId, property, limit };
  if (eventName) params.event_name = eventName;
  const trimmed = String(q || '').trim();
  if (trimmed) params.q = trimmed;
  const res = await axiosInstance.get('/events/event-property-options', { params, signal });
  if (res.data?.success && Array.isArray(res.data.options)) return res.data.options;
  return [];
}

export function useUserPropertyServerSearch({ filter, teamId, browseOptions, propertyName }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const prop = propertyName || filter?.property || filter?.field;
  const isStringish = filter?.propertyType === 'string'
    || (filter?.propertyType === 'list' && (filter?.listItemType || 'string') === 'string');
  const enabled = (filter?.propertySource === 'user' || String(prop || '').startsWith('user.') || String(prop || '').startsWith('custom.'))
    && typeof prop === 'string'
    && isStringish
    && (filter?.operator === 'is' || filter?.operator === 'is_not');

  useEffect(() => {
    if (!enabled || !teamId) return undefined;
    const q = searchQuery.trim();
    if (!q) {
      setSearchOptions([]);
      setSearchLoading(false);
      return undefined;
    }
    const ac = new AbortController();
    const timer = setTimeout(() => {
      setSearchLoading(true);
      const apiProp = String(prop).startsWith('user.') ? prop : `user.${String(prop).replace(/^custom\./, '')}`;
      fetchUserPropertyOptions(teamId, apiProp, { q, signal: ac.signal })
        .then(setSearchOptions)
        .catch(() => { if (!ac.signal.aborted) setSearchOptions([]); })
        .finally(() => { if (!ac.signal.aborted) setSearchLoading(false); });
    }, 300);
    return () => {
      clearTimeout(timer);
      ac.abort();
    };
  }, [enabled, searchQuery, teamId, prop]);

  return {
    enabled,
    searchQuery,
    setSearchQuery,
    searchLoading,
    displayOptions: searchQuery.trim() ? searchOptions : (browseOptions || []),
  };
}

export function useEventPropertyServerSearch({ filter, teamId, eventName, browseOptions }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const isStringish = filter?.propertyType === 'string'
    || (filter?.propertyType === 'list' && (filter?.listItemType || 'string') === 'string');
  const enabled = filter?.propertySource === 'event'
    && typeof filter?.property === 'string'
    && isStringish
    && (filter?.operator === 'is' || filter?.operator === 'is_not');

  useEffect(() => {
    if (!enabled || !teamId) return undefined;
    const q = searchQuery.trim();
    if (!q) {
      setSearchOptions([]);
      setSearchLoading(false);
      return undefined;
    }
    const ac = new AbortController();
    const timer = setTimeout(() => {
      setSearchLoading(true);
      fetchEventPropertyOptions(teamId, filter.property, { eventName, q, signal: ac.signal })
        .then(setSearchOptions)
        .catch(() => { if (!ac.signal.aborted) setSearchOptions([]); })
        .finally(() => { if (!ac.signal.aborted) setSearchLoading(false); });
    }, 300);
    return () => {
      clearTimeout(timer);
      ac.abort();
    };
  }, [enabled, searchQuery, teamId, filter?.property, eventName]);

  return {
    enabled,
    searchQuery,
    setSearchQuery,
    searchLoading,
    displayOptions: searchQuery.trim() ? searchOptions : (browseOptions || []),
  };
}

export function activePropertyServerSearch(userSearch, eventSearch) {
  if (userSearch?.enabled) return userSearch;
  if (eventSearch?.enabled) return eventSearch;
  return { enabled: false, displayOptions: [], setSearchQuery: () => {}, searchLoading: false };
}
